/* ============================================
   Senchado 2.0 — 二十四節気アイコン（生きてるレイヤー）
   仕様: 谷本決裁 2026-07-08（節気表現の方向修正）

   責務（JS装飾層のみ）:
     ヘッダーのロゴ横にある「二十四節気アイコン」画像を、settings CSV `sekki`
     （Liquid が data-sekki-csv に JSON 化して渡す）から「今日(JST)がどの節気か」で
     切り替える。文字は増やさない（アイコンで見せる）。
     クリック先も同じ行の解説記事URL（CSV 4列目）へ差し替える。
     4列目が無い節気は Liquid 側の href をそのまま使う（＝フォールバック）。

   節気日の決め方（2026-08-02 谷本決裁「C案」）:
     節気の日は年ごとに1日前後する（例: 立秋は 8/7 の年と 8/8 の年がある）ため、
     CSV の MMDD ではなく太陽の視黄経から毎年計算する。CSV の MMDD は
     計算できない環境のためのフォールバックとして残してある。
     日本の暦では「節気の瞬間を含む日」がその節気の初日なので、
     当日の終わり（＝翌日 0:00 JST）の黄経で今日の節気を決める。
     検証: 2021年の社内資料と 21/24 一致（不一致3件は資料が「◯日頃」の概数）。

   共通規約の順守（生きてるレイヤー規約①②③）:
     ① 成立しないなら何もしない（CSV空/不正/行ゼロ/画像なし → アイコン非表示のまま）
     ② ローテは決定的（日付から一意に決まる。ランダム/シャッフルなし）
     ③ タイムゾーンはストアTZ（Asia/Tokyo）固定。海外閲覧者もJST基準
     欠番フォールバック: 直前の（画像のある）節気を継続表示（未整備の節気で空にならない）
     年末年始 wrap: 黄経順に遡るので 1〜2月も前年の節気へ自然に繋がる

   データの持ち方（2026-08-02 谷本決裁「CSV をやめてコードで持つ」）:
     節気名・イラスト・解説記事の対応は snippets/senchado-sekki-table.liquid が持ち、
     <script type="application/json" id="sc-sekki-table"> として出力する。
     テーマ設定の CSV は不要（設定に置くと git の外に出てレビューも履歴も効かない）。
     旧 data-sekki-csv も読めるまま残してある（テーブルが無い環境のフォールバック）。

   旧 CSV 列: MMDD,節気名,画像（CDN URL）[,解説記事URL]。MM-DD 表記も許容。
     画像なしの行は捨てる（＝直前の節気アイコンをそのまま継続させる）。
     行の特定は「節気名」で行う（MMDD は月日フォールバック専用）。

   テスト: tests/sekki.spec.js（境界日・wrap・未整備継続・CSV空/不正・JST基準・アイコン適用）
   ============================================ */

(function () {
  'use strict';

  // CSV（"MMDD,節気名,画像URL" の1行1節気）を昇順ソート済み行配列へ。不正行は捨てる。
  function parseCsv(csv) {
    if (typeof csv !== 'string') return [];
    var out = [];
    var lines = csv.split(/\r?\n/);
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) continue;
      var parts = line.split(',');
      var md = (parts[0] || '').trim();
      var name = (parts[1] || '').trim();
      var image = (parts[2] || '').trim();
      // 4列目は解説記事URL（省略可）。3列だけの旧CSVでは空文字になる
      var link = (parts[3] || '').trim();
      var m = /^(\d{2})-?(\d{2})$/.exec(md); // MMDD / MM-DD の両方を許容
      if (!m) continue;
      var mm = parseInt(m[1], 10);
      var dd = parseInt(m[2], 10);
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31) continue; // 不正日付は無視
      if (!name) continue;   // 節気名なしは無視
      if (!image) continue;  // 画像なしは無視（＝直前の節気を継続させる）
      out.push({ mm: mm, dd: dd, name: name, image: image, link: link });
    }
    out.sort(function (a, b) { return (a.mm - b.mm) || (a.dd - b.dd); });
    return out;
  }

  // ストアTZ（Asia/Tokyo）での今日 {yyyy,mm,dd}。date 省略時は現在時刻。
  function jstParts(date) {
    var d = date || new Date();
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(d);
      var yyyy = 0, mm = 0, dd = 0;
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'year') yyyy = parseInt(parts[i].value, 10);
        if (parts[i].type === 'month') mm = parseInt(parts[i].value, 10);
        if (parts[i].type === 'day') dd = parseInt(parts[i].value, 10);
      }
      if (yyyy >= 1 && mm >= 1 && dd >= 1) return { yyyy: yyyy, mm: mm, dd: dd };
    } catch (e) { /* Intl 不可環境 → ローカル日付にフォールバック */ }
    return { yyyy: d.getFullYear(), mm: d.getMonth() + 1, dd: d.getDate() };
  }

  // 後方互換（旧API）: JSTでの今日 {mm,dd}
  function jstToday(date) {
    var p = jstParts(date);
    return { mm: p.mm, dd: p.dd };
  }

  // 黄経15度ごとの二十四節気（λ=0°が春分）。この並びが「暦の順序」そのもの。
  var TERMS = [
    '春分', '清明', '穀雨', '立夏', '小満', '芒種',
    '夏至', '小暑', '大暑', '立秋', '処暑', '白露',
    '秋分', '寒露', '霜降', '立冬', '小雪', '大雪',
    '冬至', '小寒', '大寒', '立春', '雨水', '啓蟄'
  ];

  function julianDay(date) { return date.getTime() / 86400000 + 2440587.5; }

  // 太陽の視黄経（度・0〜360）。Meeus の低精度式（誤差 ≒0.01度 ≒ 15分）。
  // 日単位の判定には十分な精度。
  function solarLongitude(date) {
    var T = (julianDay(date) - 2451545.0) / 36525;
    var L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T; // 平均黄経
    var M = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;  // 平均近点角
    var Mr = M * Math.PI / 180;
    var C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(Mr)
          + (0.019993 - 0.000101 * T) * Math.sin(2 * Mr)
          + 0.000289 * Math.sin(3 * Mr);                       // 中心差
    var Om = (125.04 - 1934.136 * T) * Math.PI / 180;
    var lambda = L0 + C - 0.00569 - 0.00478 * Math.sin(Om);    // 章動・光行差の補正
    return ((lambda % 360) + 360) % 360;
  }

  // 今日(JST)の節気名。日本の暦では節気の瞬間を含む日がその節気の初日なので、
  // 当日の終わり（翌日0:00 JST）の黄経で判定する。計算できなければ null。
  function termOfToday(date) {
    var p = jstParts(date);
    // Date.UTC は日/時のはみ出しを正規化する。-9時 = JST 0:00 の UTC 表現
    var endOfDay = new Date(Date.UTC(p.yyyy, p.mm - 1, p.dd + 1, -9, 0, 0));
    var lon = solarLongitude(endOfDay);
    if (!isFinite(lon)) return null;
    return TERMS[Math.floor(lon / 15) % TERMS.length] || null;
  }

  // フォールバック: 今日(JST)を過ぎた最新の開始月日の行。
  // 無ければ wrap で最後の行（前年の最後の節気を継続）。天文計算が使えない環境用。
  function resolveByMonthDay(rows, date) {
    var t = jstToday(date);
    var key = t.mm * 100 + t.dd;
    var pick = null;
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].mm * 100 + rows[i].dd <= key) pick = rows[i]; // rows は昇順 → 最後に通ったものが最新
    }
    if (!pick) pick = rows[rows.length - 1]; // 年末年始 wrap
    return pick;
  }

  // 今日(JST)の節気の行。節気日は毎年計算するので CSV の MMDD がズレていても正しく切り替わる。
  // 未整備の節気（行が無い）は黄経順に遡って直前の節気を継続表示する。
  function resolve(rows, date) {
    if (!rows || !rows.length) return null;
    var name = termOfToday(date);
    if (!name) return resolveByMonthDay(rows, date); // 計算不能 → 従来方式

    var byName = {};
    for (var i = 0; i < rows.length; i++) byName[rows[i].name] = rows[i];

    var idx = TERMS.indexOf(name);
    if (idx < 0) return resolveByMonthDay(rows, date);
    // 今日の節気から黄経順に遡る（一周して見つからなければ null）
    for (var back = 0; back < TERMS.length; back++) {
      var hit = byName[TERMS[(idx - back + TERMS.length) % TERMS.length]];
      if (hit) return hit;
    }
    return null;
  }

  // ページ内の節気テーブル（snippets/senchado-sekki-table.liquid が出力する JSON）。
  // 節気名・イラスト・解説記事はコード側が持つので、テーマ設定は要らない。
  // 見つからない/壊れている場合は null を返し、旧 CSV 方式へ落ちる（後方互換）。
  function readTable(scope) {
    var el = (scope || document).getElementById
      ? (scope || document).getElementById('sc-sekki-table')
      : null;
    if (!el) return null;
    var data;
    try { data = JSON.parse(el.textContent || ''); } catch (e) { return null; }
    if (!data || !data.length) return null;
    var out = [];
    for (var i = 0; i < data.length; i++) {
      var r = data[i];
      if (!r || !r.name || !r.image) continue; // 画像なしは捨てる＝直前の節気を継続
      out.push({ mm: 0, dd: 0, name: String(r.name), image: String(r.image), link: r.link ? String(r.link) : '' });
    }
    return out.length ? out : null;
  }

  // 1つの節気アイコン（アンカー要素）へ適用。成立しなければ何もしない（規約①）。
  function applyIcon(anchor) {
    if (!anchor) return;
    var rows = readTable(document);
    if (!rows) {
      // 旧方式: テーマ設定の CSV（data-sekki-csv）。テーブルが無い環境のフォールバック
      var raw = anchor.getAttribute('data-sekki-csv');
      if (!raw) return;                     // CSV なし → 非表示のまま
      var csv;
      try { csv = JSON.parse(raw); } catch (e) { return; } // JSON でなければ何もしない
      rows = parseCsv(csv);
    }
    if (!rows.length) return;               // 行ゼロ/全行不正 → 非表示のまま
    var row = resolve(rows);
    if (!row || !row.name || !row.image) return; // 未解決 → 非表示のまま
    var img = anchor.querySelector('img');
    if (!img) return;
    img.setAttribute('src', row.image);
    img.setAttribute('alt', '');            // 装飾扱い。ラベルはアンカーの aria-label が担う
    anchor.setAttribute('aria-label', '二十四節気 ' + row.name);
    // 解説記事URLがある節気だけ飛び先も差し替える。無ければ Liquid の href を残す
    if (row.link) anchor.setAttribute('href', row.link);
    anchor.removeAttribute('hidden');       // 確定 → 表示
  }

  // Article periods use the same astronomical calculation as the header, in JST.
  var startsByYear = {};
  function termStarts(year) {
    if (startsByYear[year]) return startsByYear[year];
    var starts = {};
    var previous = termOfToday(new Date(Date.UTC(year - 1, 11, 31, 3)));
    for (var day = 1; day <= 366; day++) {
      var date = new Date(Date.UTC(year, 0, day, 3));
      if (date.getUTCFullYear() !== year) break;
      var name = termOfToday(date);
      if (name && name !== previous) starts[name] = Date.UTC(year, date.getUTCMonth(), date.getUTCDate());
      previous = name;
    }
    startsByYear[year] = starts;
    return starts;
  }
  function termPeriod(name, year) {
    var index = TERMS.indexOf(name);
    if (index < 0 || !Number.isInteger(year) || year < 2000 || year > 2099) return null;
    var start = termStarts(year)[name];
    var nextName = TERMS[(index + 1) % TERMS.length];
    var next = termStarts(year)[nextName];
    if (next <= start) next = termStarts(year + 1)[nextName];
    if (!Number.isFinite(start) || !Number.isFinite(next)) return null;
    return { start: start, end: next - 86400000, next: next, nextName: nextName };
  }
  function periodLabel(period) {
    var weekdays = '日月火水木金土';
    function label(ms) {
      var d = new Date(ms);
      return d.getUTCFullYear() + '年' + (d.getUTCMonth()+1) + '月' + d.getUTCDate() + '日（' + weekdays[d.getUTCDay()] + '）';
    }
    return label(period.start) + '〜' + label(period.end) + '頃';
  }
  function applyArticle(root, date) {
    var scope = root || document;
    var today = jstParts(date);
    var articles = scope.querySelectorAll('[data-sc-sekki-article]');
    for (var i = 0; i < articles.length; i++) {
      var article = articles[i];
      var name = article.getAttribute('data-sc-sekki-article');
      var year = today.yyyy;
      // In early January the current winter solstice began in December of last year.
      if (name === '冬至' && today.mm === 1 && termOfToday(date) === '冬至') year--;
      var current = termPeriod(name, year);
      if (!current) continue;
      var next = termPeriod(current.nextName, new Date(current.next).getUTCFullYear());
      var headings = article.querySelectorAll('h2, h3');
      for (var h = 0; h < headings.length; h++) {
        var text = headings[h].textContent;
        var match = /^([^（()\d]+[（(][^）)]+[）)])(?:\d{4}年)?\d{1,2}月\d{1,2}日/.exec(text);
        if (!match) continue;
        var headingName = text.split(/[（(]/)[0].trim();
        var period = headingName === name ? current : headingName === current.nextName ? next : null;
        if (period) headings[h].textContent = match[1] + periodLabel(period);
      }
    }
  }

  // ページ内すべての節気アイコンへ適用。
  function apply(root) {
    var scope = root || document;
    var icons = scope.querySelectorAll('[data-sc-sekki-icon]');
    for (var i = 0; i < icons.length; i++) applyIcon(icons[i]);
  }

  function boot() { apply(document); applyArticle(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme Editor（セクション再描画）で再適用
  document.addEventListener('shopify:section:load', function () { apply(document); applyArticle(document); });

  // テスト用途に純粋関数を公開（DOM非依存）
  window.SenchadoSekki = {
    parseCsv: parseCsv,
    readTable: readTable,
    jstToday: jstToday,
    jstParts: jstParts,
    solarLongitude: solarLongitude,
    termOfToday: termOfToday,
    resolveByMonthDay: resolveByMonthDay,
    resolve: resolve,
    apply: apply,
    applyIcon: applyIcon,
    termPeriod: termPeriod,
    periodLabel: periodLabel,
    applyArticle: applyArticle,
    TERMS: TERMS
  };
})();
